import React, { useEffect, useState, useRef } from "react";
import { CalendarDays, Sun, Moon } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";

const LANGS = [
  { code: "en", flag: "🇬🇧", name: "English"      },
  { code: "tr", flag: "🇹🇷", name: "Türkçe"       },
  { code: "de", flag: "🇩🇪", name: "Deutsch"      },
  { code: "fr", flag: "🇫🇷", name: "Français"     },
  { code: "es", flag: "🇪🇸", name: "Español"      },
  { code: "ar", flag: "🇸🇦", name: "العربية"      },
  { code: "ru", flag: "🇷🇺", name: "Русский"      },
  { code: "zh", flag: "🇨🇳", name: "中文"          },
  { code: "ja", flag: "🇯🇵", name: "日本語"        },
  { code: "pt", flag: "🇧🇷", name: "Português"    },
  { code: "it", flag: "🇮🇹", name: "Italiano"     },
  { code: "ko", flag: "🇰🇷", name: "한국어"        },
  { code: "hi", flag: "🇮🇳", name: "हिन्दी"         },
  { code: "nl", flag: "🇳🇱", name: "Nederlands"   },
  { code: "pl", flag: "🇵🇱", name: "Polski"       },
  { code: "sv", flag: "🇸🇪", name: "Svenska"      },
  { code: "no", flag: "🇳🇴", name: "Norsk"        },
  { code: "da", flag: "🇩🇰", name: "Dansk"        },
  { code: "fi", flag: "🇫🇮", name: "Suomi"        },
  { code: "el", flag: "🇬🇷", name: "Ελληνικά"     },
];

const EN_MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const TR_MONTHS = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık",
];

function formatDateTime(d, lang) {
  const months = lang === "tr" ? TR_MONTHS : EN_MONTHS;
  const day = d.getDate();
  const month = months[d.getMonth()];
  const year = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return { date: `${day} ${month} ${year}`, time: `${hh}:${mm}` };
}

export default function HeaderBar({ conditions, setConditions }) {
  const { lang, setLang, t } = useI18n();
  const [dt, setDt] = useState(() => formatDateTime(new Date(), lang));
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);

  // Refresh every 30 s so the floating clock reflects the wall time.
  useEffect(() => {
    setDt(formatDateTime(new Date(), lang));
    const tick = setInterval(() => setDt(formatDateTime(new Date(), lang)), 30000);
    return () => clearInterval(tick);
  }, [lang]);

  // Click-outside to close the language dropdown
  useEffect(() => {
    if (!langOpen) return;
    const onDocClick = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [langOpen]);

  const pickLang = (l) => {
    setLangOpen(false);
    setLang(l.code);
  };

  const current = LANGS.find((x) => x.code === lang) || LANGS[0];

  const mode = conditions?._mode === "night" ? "night" : "day";

  // Sync timeOfDay → <html> class so global CSS overrides can paint the light theme.
  // Also forces body background/foreground to flip immediately (inline styles win
  // over the body baseline set by HeatPortal).
  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    const body = document.body;
    if (mode === "day") {
      root.classList.remove("dark");
      root.classList.add("light");
      body.style.background = "#FFFFFF";
      body.style.color = "#0F172A";
    } else {
      root.classList.remove("light");
      root.classList.add("dark");
      body.style.background = "#000000";
      body.style.color = "#FFFFFF";
    }
  }, [mode]);

  // Spec: DAY = +20 outdoor / +22 indoor / 60% out / 50% in.
  //       NIGHT = -5 outdoor / +20 indoor / 80% out / 55% in.
  // These presets dramatically shift the Glaser curve + condensation risk so the
  // user sees an immediate change in the calculation/charts when toggling.
  const setDay = () => setConditions && setConditions({
    ...conditions,
    outdoor_temp: 20, indoor_temp: 22,
    outdoor_humidity: 60, indoor_humidity: 50,
    _mode: "day",
  });
  const setNight = () => setConditions && setConditions({
    ...conditions,
    outdoor_temp: -5, indoor_temp: 20,
    outdoor_humidity: 80, indoor_humidity: 55,
    _mode: "night",
  });

  return (
    <header className="relative border-b border-[#1F1F1F] bg-black" data-testid="header-bar">
      {/* TOP-LEFT — Live date/time — ALWAYS white on the always-black header */}
      <div className="absolute top-3 left-4 sm:left-6 lg:left-8"
        style={{ zIndex: 9999, opacity: 1, visibility: "visible" }}>
        <div
          style={{
            color: "#FFFFFF",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "7px",
            opacity: 1,
            visibility: "visible",
            position: "relative",
            zIndex: 9999,
          }}
          data-testid="header-date">
          {/* Date row — icon + date text on the same baseline */}
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "5px",
              lineHeight: 1,
            }}>
            <CalendarDays
              style={{
                width: "14px",
                height: "14px",
                color: "#FFFFFF",
                stroke: "#FFFFFF",
                flexShrink: 0,
                display: "block",
              }}/>
            <span
              data-testid="header-date-line"
              style={{
                color: "#FFFFFF",
                fontSize: "14px",
                fontWeight: 400,
                fontFamily: "'Inter', system-ui, sans-serif",
                letterSpacing: "0.5px",
                opacity: 0.95,
                lineHeight: 1,
                display: "flex",
                alignItems: "center",
              }}>
              {dt.date}
            </span>
          </div>
          {/* Time row — separate line below the date row */}
          <span
            data-testid="header-time-line"
            style={{
              color: "#FFFFFF",
              fontSize: "15px",
              fontWeight: 500,
              fontFamily: "'Inter', system-ui, sans-serif",
              letterSpacing: "0.3px",
              opacity: 1,
              lineHeight: 1,
              paddingLeft: "19px",
            }}>
            {dt.time}
          </span>
        </div>
      </div>

      {/* TOP-RIGHT — Elite theme toggle + LNG dropdown · horizontally aligned jewel pair */}
      <div className="absolute top-3 right-3 sm:right-4 lg:right-6 flex flex-row items-center gap-3 z-10" style={{ flexShrink: 0 }}>
        {/* Elite Theme Toggle — pill switch · iPhone Control Center vibe */}
        <div
          role="switch"
          aria-checked={mode === "night"}
          tabIndex={0}
          onClick={() => (mode === "day" ? setNight() : setDay())}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); mode === "day" ? setNight() : setDay(); } }}
          data-testid="header-theme-toggle"
          style={{
            width: '52px',
            height: '26px',
            borderRadius: '9999px',
            backgroundColor: mode === "night" ? '#1E293B' : '#F59E0B',
            position: 'relative',
            cursor: 'pointer',
            transition: 'background-color 300ms ease-in-out, box-shadow 300ms ease-in-out',
            border: '1px solid rgba(255,255,255,0.2)',
            boxShadow: mode === "night"
              ? '0 2px 6px rgba(0,0,0,0.5), 0 0 10px rgba(59,130,246,0.25)'
              : '0 2px 6px rgba(0,0,0,0.3), 0 0 10px rgba(245,158,11,0.45)',
            display: 'inline-flex',
            alignItems: 'center',
            flexShrink: 0,
            outline: 'none',
          }}
          onFocus={(e) => { e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0, 229, 255, 0.45)'; }}
          onBlur={(e) => { e.currentTarget.style.boxShadow = mode === "night"
            ? '0 2px 6px rgba(0,0,0,0.5), 0 0 10px rgba(59,130,246,0.25)'
            : '0 2px 6px rgba(0,0,0,0.3), 0 0 10px rgba(245,158,11,0.45)'; }}>
          <div style={{
            width: '22px',
            height: '22px',
            borderRadius: '50%',
            backgroundColor: '#FFFFFF',
            position: 'absolute',
            top: '1px',
            left: mode === "night" ? '28px' : '2px',
            transition: 'left 300ms ease-in-out, transform 500ms ease-in-out',
            transform: mode === "night" ? 'rotate(360deg)' : 'rotate(0deg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
          }}>
            {mode === "night"
              ? <Moon className="w-3 h-3" style={{ color: '#1E293B' }}/>
              : <Sun  className="w-3 h-3" style={{ color: '#F59E0B' }}/>}
          </div>
        </div>

        {/* Language — ultra-minimalist text trigger ("EN ▾") · rich dropdown */}
        <div ref={langRef} className="relative">
          <button
            type="button"
            onClick={() => setLangOpen((v) => !v)}
            data-testid="language-toggle"
            aria-label="Choose language"
            aria-expanded={langOpen}
            className="lang-trigger"
            style={{
              background: "none",
              border: "none",
              padding: "4px 8px",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              opacity: langOpen ? 1 : 0.9,
              transition: "opacity 160ms ease",
              position: "relative",
              zIndex: 9999,
              visibility: "visible",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = 1; }}
            onMouseLeave={(e) => { if (!langOpen) e.currentTarget.style.opacity = 0.9; }}>
            <svg
              width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              aria-hidden="true"
              style={{
                color: '#3B82F6',
                stroke: '#3B82F6',
                fill: 'none',
                filter: 'drop-shadow(0 0 4px #3B82F6) drop-shadow(0 0 8px rgba(59, 130, 246, 0.5))'
              }}>
              <circle cx="12" cy="12" r="10" stroke="#3B82F6"/>
              <path d="M2 12h20" stroke="#3B82F6"/>
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" stroke="#3B82F6"/>
            </svg>
            <span
              style={{
                color: "#FFFFFF",
                fontSize: "12px",
                fontWeight: 400,
                fontFamily: "'Inter', 'Montserrat', system-ui, sans-serif",
                letterSpacing: "1px",
                textTransform: "uppercase",
                lineHeight: 1,
              }}>
              LNG
            </span>
          </button>

          {langOpen && (
            <div
              role="listbox"
              data-testid="language-dropdown"
              className="lang-dropdown"
              style={{
                position: "absolute",
                top: "calc(100% + 8px)",
                right: 0,
                minWidth: "232px",
                maxHeight: "340px",
                overflowY: "auto",
                padding: "8px",
                background: "#0F0F1A",
                borderRadius: "12px",
                border: "2px solid #1E40AF",
                boxShadow: "0 10px 40px rgba(0,0,0,0.55), 0 0 0 1px rgba(30,64,175,0.35), 0 0 18px rgba(30,64,175,0.25)",
                zIndex: 60,
                animation: "langDropdownIn 220ms cubic-bezier(0.22,0.61,0.36,1)",
              }}>
              <div
                style={{
                  padding: "4px 12px 8px",
                  fontSize: "10px",
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: "#7AA0FF",
                  fontFamily: "Inter, system-ui, sans-serif",
                  fontWeight: 700,
                }}>
                {t("header.choose_lang", "Choose language")}
              </div>
              {LANGS.map((l) => {
                const active = l.code === lang;
                return (
                  <button
                    key={l.code}
                    type="button"
                    onClick={() => pickLang(l)}
                    role="option"
                    aria-selected={active}
                    data-testid={`language-opt-${l.code}`}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "9px 12px",
                      cursor: "pointer",
                      borderRadius: "8px",
                      border: "none",
                      textAlign: "left",
                      background: active ? "rgba(30,64,175,0.22)" : "transparent",
                      boxShadow: active ? "inset 0 0 0 1px rgba(30,64,175,0.85)" : "none",
                      transition: "background 140ms ease",
                    }}
                    onMouseEnter={(e) => {
                      if (!active) e.currentTarget.style.background = "rgba(30,64,175,0.14)";
                    }}
                    onMouseLeave={(e) => {
                      if (!active) e.currentTarget.style.background = "transparent";
                    }}>
                    <span style={{ fontSize: "20px", lineHeight: 1, width: "22px", textAlign: "center" }}>{l.flag}</span>
                    <span
                      style={{
                        flex: 1,
                        fontFamily: "Inter, system-ui, sans-serif",
                        fontWeight: 500,
                        fontSize: "14px",
                        letterSpacing: "0.2px",
                        color: l.supported ? "#FFFFFF" : "#A6A6B0",
                      }}>
                      {l.name}
                    </span>
                    {!l.supported && (
                      <span
                        style={{
                          fontSize: "10px",
                          padding: "2px 8px",
                          borderRadius: "999px",
                          background: "rgba(30,64,175,0.18)",
                          color: "#7AA0FF",
                          fontWeight: 700,
                          fontFamily: "Inter, system-ui, sans-serif",
                          letterSpacing: "0.2px",
                        }}>
                        {t("header.coming_soon_short", "Soon")}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* HEROIC CENTERPIECE TITLE */}
      <div className="flex flex-col items-center justify-center w-full py-10 px-4" style={{ backgroundColor: "#000000" }}>
        <h1
          className="text-5xl md:text-6xl font-black uppercase tracking-widest text-center neon-pulse-title"
          style={{
            marginBottom: "12px",
            lineHeight: "1.2",
            animation: "neonPulse 2.5s ease-in-out infinite",
          }}
          data-testid="brand-title">
          THERMAL ACADEMY
        </h1>
        <p
          className="text-sm md:text-[15px] font-normal italic text-center mx-auto"
          style={{
            color: "#FFFFFF",
            maxWidth: "600px",
            lineHeight: 1.4,
            letterSpacing: "0.5px",
            marginTop: "8px",
            textShadow: "0 0 8px rgba(255,255,255,0.25)",
            opacity: 0.95,
          }}
          data-testid="brand-subtitle">
          {t("app.subtitle.tagline", "AI-Powered Smart Platform Shaping the Future of Global Insulation")}
        </p>
      </div>
    </header>
  );
}
